import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { formatBytes } from '@/utils/formatBytes';

dayjs.extend(relativeTime);

/**
 * Pure formatting for the record Details displays, one function per kind that
 * needs more than the plain value. The components in this folder call these.
 */

const NUMBER_FORMAT = new Intl.NumberFormat('en-US', { maximumFractionDigits: 3 });

/** A number as a number: a finite number, or a numeric string such as "12". Anything else is null. */
export function numericValueOf(rawValue: unknown): number | null {
    if (typeof rawValue === 'number') {
        return Number.isFinite(rawValue) ? rawValue : null;
    }
    if (typeof rawValue === 'string' && rawValue.trim() !== '') {
        const parsedValue = Number(rawValue);
        return Number.isFinite(parsedValue) ? parsedValue : null;
    }
    return null;
}

/** Grouped: "1,204". */
export function formatGroupedNumber(numericValue: number): string {
    return NUMBER_FORMAT.format(numericValue);
}

/**
 * "11 PM" for 23, "12 AM" for 0. Null for anything that cannot be an hour of
 * the day, such as 5748718, so the display can show the raw number in amber.
 */
export function formatHourOfDay(numericValue: number): string | null {
    if (!Number.isInteger(numericValue) || numericValue < 0 || numericValue > 23) {
        return null;
    }
    const hourOnClock = numericValue % 12 === 0 ? 12 : numericValue % 12;
    return `${hourOnClock} ${numericValue < 12 ? 'AM' : 'PM'}`;
}

/**
 * Seconds as a clock: 181 is "3:01", 3725 is "1:02:05". A fraction of a
 * second keeps one decimal, so a crossfade of 2.5 seconds reads "0:02.5"
 * rather than rounding up to 3.
 */
export function formatDuration(totalSeconds: number): string {
    const sign = totalSeconds < 0 ? '-' : '';
    const absoluteSeconds = Math.abs(totalSeconds);
    const hours = Math.floor(absoluteSeconds / 3600);
    const minutes = Math.floor((absoluteSeconds % 3600) / 60);
    const roundedSeconds = Math.round((absoluteSeconds % 60) * 10) / 10;
    const wholeSeconds = Math.floor(roundedSeconds);
    const tenths = Math.round((roundedSeconds - wholeSeconds) * 10);
    const secondsText = `${String(wholeSeconds).padStart(2, '0')}${tenths > 0 ? `.${tenths}` : ''}`;
    if (hours > 0) {
        return `${sign}${hours}:${String(minutes).padStart(2, '0')}:${secondsText}`;
    }
    return `${sign}${minutes}:${secondsText}`;
}

/** "12 GB" for a field in gigabytes, "192 kbps" for a bitrate, and a byte size for anything else. */
export function formatSizeOrRate(fieldName: string, numericValue: number): string {
    const words = fieldName.split('_');
    if (words.includes('gb')) {
        return `${formatGroupedNumber(numericValue)} GB`;
    }
    if (words.includes('bitrate')) {
        return `${formatGroupedNumber(numericValue)} kbps`;
    }
    return formatBytes(numericValue);
}

/** How full a thin bar is drawn, from 0 to 100. */
export function barPercent(numericValue: number): number {
    return Math.min(100, Math.max(0, numericValue));
}

/**
 * The address a link value points at, and the domain it shows: "gibson.com"
 * for "http://www.gibson.com/non-ut". A bare "gibson.com" gets https. Null
 * when the value is not a web address at all.
 */
export function parseLink(rawValue: string): { href: string; domain: string } | null {
    const trimmedValue = rawValue.trim();
    const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(trimmedValue) ? trimmedValue : `https://${trimmedValue}`;
    try {
        const url = new URL(withScheme);
        if (url.protocol !== 'http:' && url.protocol !== 'https:') {
            return null;
        }
        if (!url.hostname.includes('.') && url.hostname !== 'localhost') {
            return null;
        }
        return { href: url.href, domain: url.hostname.replace(/^www\./, '') };
    } catch {
        return null;
    }
}

/** "FK Fallback Seed User" is "FU": the first letters of the first and the last word. */
export function initialsOf(name: string): string {
    const words = name.trim().split(/\s+/).filter((word) => word !== '');
    if (words.length === 0) {
        return '?';
    }
    const first = words[0].charAt(0);
    const last = words.length > 1 ? words[words.length - 1].charAt(0) : '';
    return `${first}${last}`.toUpperCase();
}

/**
 * "Oct 12, 2026 at 10:19 PM", in the viewer's own time. A plain calendar date
 * such as "2026-10-12" has no time to show, so it reads "Oct 12, 2026", and is
 * read in UTC so it does not move to the day before west of Greenwich.
 */
export function formatDateTime(rawValue: string | number | Date): string | null {
    if (typeof rawValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(rawValue.trim())) {
        const calendarDate = dayjs(`${rawValue.trim()}T12:00:00`);
        return calendarDate.isValid() ? calendarDate.format('MMM D, YYYY') : null;
    }
    const dateValue = dayjs(rawValue);
    return dateValue.isValid() ? dateValue.format('MMM D, YYYY [at] h:mm A') : null;
}

/** "3 days ago", for the hover text of a date. */
export function timeAgo(rawValue: string | number | Date, now: Date = new Date()): string | null {
    const dateValue = dayjs(rawValue);
    return dateValue.isValid() ? dateValue.from(dayjs(now)) : null;
}

/** "2:14 PM" in that time zone, or null for a zone the browser does not know. */
export function timeInZone(timeZone: string, now: Date = new Date()): string | null {
    try {
        return new Intl.DateTimeFormat('en-US', { timeZone, hour: 'numeric', minute: '2-digit' }).format(now);
    } catch {
        return null;
    }
}

/** The text of one item in a list of values. An object reads by its name, or as compact JSON. */
export function listItemText(item: unknown): string {
    if (item === null || item === undefined) {
        return '—';
    }
    if (typeof item === 'object') {
        const record = item as Record<string, unknown>;
        const name = record.display_string ?? record.name;
        if (typeof name === 'string' && name !== '') {
            return name;
        }
        return JSON.stringify(item);
    }
    return String(item);
}
